/**
 * SAMDUO shared GSAP 3.12.5 loader (singleton).
 * Ensures gsap + ScrollTrigger are injected only once per page.
 *
 * Also provides a debounced ScrollTrigger.refresh() coordinator
 * so that multiple sections can request refreshes without
 * redundant layout recalculations.
 */
(function (w) {
  'use strict';

  // This snippet is rendered by every section that uses GSAP, so one page can
  // emit the tag 5-8 times. The IIFE keeps module-level state (_resizeListenerAttached,
  // _sectionRegistry) that would be reset and re-registered on each extra run,
  // leaving duplicate resize listeners and orphaned section entries behind.
  if (w.__samduoGsapLoaded) return;
  w.__samduoGsapLoaded = true;

  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var ST_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.onload = function () {
        resolve();
      };
      el.onerror = reject;
      document.head.appendChild(el);
    });
  }

  w.samduoLoadGSAP = function () {
    if (w.gsap && w.ScrollTrigger) {
      w.gsap.registerPlugin(w.ScrollTrigger);
      return Promise.resolve();
    }

    if (w.__samduoGsapPromise) {
      return w.__samduoGsapPromise;
    }

    w.__samduoGsapPromise = loadScript(GSAP_URL)
      .then(function () {
        return loadScript(ST_URL);
      })
      .then(function () {
        w.gsap.registerPlugin(w.ScrollTrigger);
      })
      .catch(function (err) {
        w.__samduoGsapPromise = null;
        throw err;
      });

    return w.__samduoGsapPromise;
  };

  /* -------------------------------------------------------
   * Debounced ScrollTrigger.refresh() coordinator.
   *
   * Multiple section scripts each call ScrollTrigger.refresh()
   * at init time and on resize, which causes redundant layout
   * recalculations and animation glitches.  This helper batches
   * them into a single debounced call.
   * ------------------------------------------------------- */
  var _refreshTimer = null;

  /**
   * Debounced ScrollTrigger.refresh().
   *
   * @param {boolean|{ safe?: boolean }} [options]
   *   - 默认 `safe: false`，使用标准 refresh
   *   - 仅 pin 区段末尾需要时传 `{ safe: true }`（如 scroll-morph.js）
   *
   * Pin section 审计（经 samduoRefreshScrollTrigger 协调）：
   * | 文件 | pin | pinSpacing | 动态 end | 备注 |
   * | scroll-morph.js (E6000-10-11) | viewport | false | track bottom | track 固定高度 |
   * | E6000-13-15.liquid | inner(≥769px) | false/true | bottom | 移动无 pin |
   * | P2800-7.liquid | section(桌面) | false | +=320% | gsap.context + Registry |
   * | P2800-5.liquid | sticky | 缺 | bottom/+=N*vh | gsap.context + Registry |
   * | E6000-3-4.liquid | 无 | — | bottom | clip-path 按 progress |
   *
   * 裸调 ScrollTrigger.refresh()（未走 coordinator，resize 时需单独审查）：
   * P2800-3.liquid (pinSpacing:false),
   * brand-scroll-story.liquid, brand-2-7.liquid, brand-3.liquid, Brand-2.liquid,
   * waterfall-card-3.liquid
   */
  w.samduoRefreshScrollTrigger = function (options) {
    var safe = false;

    if (options === true) {
      safe = true;
    } else if (options && typeof options === 'object' && options.safe) {
      safe = true;
    }

    clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(function () {
      if (typeof w.ScrollTrigger !== 'undefined') {
        w.ScrollTrigger.refresh(safe);
      }
    }, 120);
  };

  /* -------------------------------------------------------
   * Centralised resize handler for GSAP sections.
   *
   * Instead of each section adding its own resize listener
   * with different debounce timings, they can all call
   * samduoOnResize(callback) to register a handler that
   * fires after a unified 200 ms debounce.
   * ------------------------------------------------------- */
  var _resizeTimer = null;
  var _resizeCallbacks = [];
  var _resizeListenerAttached = false;

  function runResizeCallbacks() {
    var cbs = _resizeCallbacks.slice();
    for (var i = 0; i < cbs.length; i++) {
      if (!cbs[i] || cbs[i].removed) continue;
      try {
        cbs[i].fn();
      } catch (e) {
        /* swallow */
      }
    }
  }

  function attachResizeListener() {
    if (_resizeListenerAttached) return;
    _resizeListenerAttached = true;
    w.addEventListener('resize', function () {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(runResizeCallbacks, 200);
    });
  }

  w.samduoOnResize = function (cb) {
    if (typeof cb !== 'function') {
      return function () {};
    }

    var entry = { fn: cb, removed: false };
    _resizeCallbacks.push(entry);
    attachResizeListener();

    return function unsubscribe() {
      entry.removed = true;
      _resizeCallbacks = _resizeCallbacks.filter(function (item) {
        return item && !item.removed;
      });
    };
  };

  /* -------------------------------------------------------
   * Section Registry — 统一管理 GSAP section 的生命周期。
   *
   * 用法（在各 section 内联 script 中）：
   *
   *   samduoRegisterSection('my-section-id', {
   *     init:    function () { ... GSAP 初始化 ... },
   *     destroy: function () { ... 清理 ... },
   *   });
   *
   * Registry 自动处理：
   *   - 防重复注册（先 destroy 再 init）
   *   - shopify:section:unload → destroy
   *   - shopify:section:load  → init
   *   - resize → destroy → init（需自行在 init 中重建）
   * ------------------------------------------------------- */
  var _sectionRegistry = new Map();

  function destroyRegistered(id) {
    var entry = _sectionRegistry.get(id);
    if (!entry || !entry.ready) return;

    try {
      if (typeof entry.handlers.destroy === 'function') {
        entry.handlers.destroy();
      }
    } catch (e) {
      /* swallow */
    }

    entry.ready = false;
  }

  function initRegistered(id) {
    var entry = _sectionRegistry.get(id);
    if (!entry || entry.ready) return;

    try {
      if (typeof entry.handlers.init === 'function') {
        entry.handlers.init();
      }
    } catch (e) {
      /* swallow */
    }

    entry.ready = true;
  }

  /**
   * 注册一个 GSAP section 的 init/destroy 生命周期。
   *
   * @param {string} sectionId  — section DOM id（如 'scroll-story-xxx'）
   * @param {{ init: Function, destroy: Function }} handlers
   */
  w.samduoRegisterSection = function (sectionId, handlers) {
    if (!sectionId || !handlers) return;

    // 如果已注册，先销毁再重建
    if (_sectionRegistry.has(sectionId)) {
      destroyRegistered(sectionId);
    }

    _sectionRegistry.set(sectionId, {
      handlers: handlers,
      ready: false,
    });

    // 立即初始化
    initRegistered(sectionId);
  };

  /**
   * 注销一个 section（完全移除注册）。
   */
  w.samduoUnregisterSection = function (sectionId) {
    if (!_sectionRegistry.has(sectionId)) return;
    destroyRegistered(sectionId);
    _sectionRegistry.delete(sectionId);
  };

  /* 自动监听 Shopify Editor 生命周期事件 */
  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (!target) return;

    _sectionRegistry.forEach(function (entry, id) {
      var el = document.getElementById(id)
        || target.querySelector('[data-section-id="' + id + '"]');
      if (el && target.contains(el)) {
        destroyRegistered(id);
      }
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target) return;

    _sectionRegistry.forEach(function (entry, id) {
      var el = document.getElementById(id)
        || target.querySelector('[data-section-id="' + id + '"]');
      if (el && target.contains(el) && !entry.ready) {
        initRegistered(id);
      }
    });
  });
})(window);
